<template>
  <v-container class="notifications" fluid>
    <!-- Page Title -->
    <div class="my-6">
      <span>
        <h2>Notifications</h2>
      </span>
    </div>

    <!-- Notification Form Card -->
    <v-card class="notification-card mx-auto mb-6" elevation="2">
      <v-card-title>
        <v-toolbar flat>
          <v-toolbar-title>
            <v-icon left color="black" class="align-center">
              mdi-send-outline
            </v-icon>
            Send Notification
          </v-toolbar-title>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="form" lazy-validation v-model="valid">
            <!-- Title Field -->
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  label="Title"
                  v-model="notification.title"
                  dense
                  required
                  :rules="titleRules"
                  validate-on-blur
                  outlined
                  hide-details="auto"
                  counter="100">
                </v-text-field>
              </v-col>
            </v-row>

            <!-- Message Field -->
            <v-row dense>
              <v-col cols="12">
                <v-textarea
                  label="Message"
                  v-model="notification.message"
                  dense
                  required
                  :rules="messageRules"
                  validate-on-blur
                  outlined
                  hide-details="auto"
                  counter="500"
                  rows="6"
                  auto-grow>
                </v-textarea>
              </v-col>
            </v-row>

            <!-- Store Group Dropdown -->
            <v-row dense>
              <v-col cols="12">
                <v-select
                  label="Store Groups"
                  v-model="selectedStoreGroups"
                  :items="storeGroups"
                  :rules="storeGroupRules"
                  :loading="loadingStoreGroups"
                  :disabled="loadingStoreGroups"
                  item-text="text"
                  item-value="value"
                  return-object
                  dense
                  required
                  outlined
                  hide-details="auto"
                  multiple
                  small-chips
                  deletable-chips
                  @change="onStoreGroupChange">
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="toggleSelectAll">
                      <v-list-item-action>
                        <v-icon :color="selectedStoreGroups.length > 0 ? 'primary' : ''">
                          {{ selectAllIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      small
                      close
                      @click:close="removeStoreGroup(index)">
                      {{ item.text }}
                    </v-chip>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row dense class="mt-4">
              <v-col>
                <v-btn
                  color="primary"
                  :disabled="!valid || loading"
                  :loading="loading"
                  @click="validateAndSubmit">
                  Send Notification
                </v-btn>
                <v-btn text class="ml-2" @click="resetForm" :disabled="loading">
                  Reset
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Notification History Card -->
    <!-- <v-card class="notification-history-card mx-auto" elevation="2">
      <v-card-title>
        <v-toolbar flat>
          <v-toolbar-title>
            <v-icon left color="black" class="align-center">
              mdi-history
            </v-icon>
            Notification History
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            outlined
            class="mr-4"
            style="max-width: 300px">
          </v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                :loading="historyLoading"
                @click="fetchNotificationHistory"
                v-bind="attrs"
                v-on="on">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh notification history</span>
          </v-tooltip>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="notificationHistory"
          :search="search"
          :loading="historyLoading"
          :items-per-page="10"
          class="elevation-0">
          <template v-slot:item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" dark small>
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:item.message="{ item }">
            <div class="text-truncate" style="max-width: 300px">
              {{ item.message }}
            </div>
          </template>
          <template v-slot:item.sentDate="{ item }">
            {{ item.sentDate ? formatDateTime(item.sentDate) : '-' }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="viewNotification(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card> -->

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Confirm Send Notification
        </v-card-title>
        <v-card-text>
          <div class="text-body-1 mb-2">
            Are you sure you want to send this notification?
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="mb-2">
            <strong>Title:</strong> {{ notification.title }}
          </div>
          <div class="mb-2">
            <strong>Message:</strong> {{ notification.message }}
          </div>
          <div>
            <strong>Recipients:</strong> {{ selectedStoreGroups.length }} store group(s)
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            @click="confirmSend">
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Notification Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedNotification">
          <v-container>
            <v-row dense>
              <v-col cols="12">
                <div class="text-subtitle-2 grey--text">Status</div>
                <v-chip
                  :color="getStatusColor(selectedNotification.status)"
                  dark
                  small
                  class="mt-1">
                  {{ selectedNotification.status }}
                </v-chip>
              </v-col>
              <v-col cols="12" class="mt-4">
                <div class="text-subtitle-2 grey--text">Title</div>
                <div class="text-body-1 mt-1">
                  {{ selectedNotification.title }}
                </div>
              </v-col>
              <v-col cols="12" class="mt-4">
                <div class="text-subtitle-2 grey--text">Message</div>
                <div class="text-body-1 mt-1" style="white-space: pre-wrap">
                  {{ selectedNotification.message }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Created</div>
                <div class="text-body-2 mt-1">
                  {{ formatDateTime(selectedNotification.createdDate) }}
                </div>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="mt-4"
                v-if="selectedNotification.sentDate">
                <div class="text-subtitle-2 grey--text">Sent</div>
                <div class="text-body-2 mt-1">
                  {{ formatDateTime(selectedNotification.sentDate) }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Recipients</div>
                <div class="text-body-2 mt-1">
                  {{ selectedNotification.recipients }}
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import notification from '@/axios/notification'
import Search from '@/axios/search'

export default {
  name: 'Notifications',
  data() {
    return {
      valid: true,
      loading: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      notification: {
        title: '',
        message: ''
      },
      selectedStoreGroups: [],
      storeGroups: [],
      loadingStoreGroups: false,
      titleRules: [
        v => !!v || 'Title is required',
        v => (v && v.length <= 100) || 'Title must be 100 characters or less'
      ],
      messageRules: [
        v => !!v || 'Message is required',
        v => (v && v.length <= 500) || 'Message must be 500 characters or less',
        v => (v && v.length >= 10) || 'Message must be at least 10 characters'
      ],
      storeGroupRules: [v => (v && v.length > 0) || 'At least one Store Group is required'],
      search: '',
      historyLoading: false,
      confirmDialog: false,
      viewDialog: false,
      selectedNotification: null,
      headers: [
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Message', value: 'message', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Recipients', value: 'recipients', sortable: false },
        { text: 'Sent', value: 'sentDate', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ],
      notificationHistory: []
    }
  },
  computed: {
    selectAllIcon() {
      if (this.storeGroups.length > 0 && this.selectedStoreGroups.length === this.storeGroups.length) {
        return 'mdi-close-box'
      }
      if (this.selectedStoreGroups.length > 0) {
        return 'mdi-minus-box'
      }
      return 'mdi-checkbox-blank-outline'
    }
  },
  methods: {
    toggleSelectAll() {
      this.$nextTick(() => {
        if (this.selectedStoreGroups.length === this.storeGroups.length) {
          this.selectedStoreGroups = []
        } else {
          this.selectedStoreGroups = [...this.storeGroups]
        }
        this.$refs.form.validate()
      })
    },
    async validateAndSubmit() {
      const isValid = this.$refs.form.validate()
      if (!isValid) {
        return
      }
      
      // Show confirmation dialog
      this.confirmDialog = true
    },
    async confirmSend() {
      this.confirmDialog = false
      
      try {
        this.loading = true

        const totalToSend = this.selectedStoreGroups.length

        // Simulate API call
        const result = await this.submitNotification()

        if (result.errors === 0) {
          this.showSnackbar('success', `Notification sent successfully to all ${result.sent} store groups!`)
        } else if (result.sent > 0) {
          this.showSnackbar('warning', `Partially sent: ${result.sent} succeeded, ${result.errors} failed out of ${totalToSend} total.`)
        } else {
          this.showSnackbar('error', `Failed to send to all ${totalToSend} store groups.`)
        }

        if (result.sent > 0) {
          this.resetForm()
        }
      } catch (err) {
        this.showSnackbar('error', err.message || 'An error occurred')
      } finally {
        this.loading = false
      }
    },
    async submitNotification() {
      const totalToSend = this.selectedStoreGroups.length
      console.log(`Preparing to send notifications to ${totalToSend} store groups`)
      console.log('Selected Store Groups:', this.selectedStoreGroups)
      
      const results = []
      const errors = []
      
      // Send a notification for each selected store group
      for (const storeGroup of this.selectedStoreGroups) {
        try {
          const payload = {
            title: this.notification.title,
            body: this.notification.message,
            upstream_party_id: storeGroup.value,
            upstream_party_name: storeGroup.text
          }
          console.log('Sending notification payload:', payload)
          
          const response = await notification.sendNotification(payload)
          console.log(`Notification sent successfully to ${storeGroup.text}:`, response)
          results.push({ storeGroup: storeGroup.text, success: true })
        } catch (error) {
          console.error(`Failed to send notification to ${storeGroup.text}:`, error)
          errors.push({ storeGroup: storeGroup.text, error: error.response?.data?.message || error.message })
        }
      }
      
      // Log summary
      console.log(`Summary: Total: ${totalToSend}, Sent: ${results.length}, Failed: ${errors.length}`)
      
      // Report results
      if (errors.length > 0) {
        const errorDetails = errors.map(e => `  - ${e.storeGroup}: ${e.error}`).join('\n')
        console.error(`Failed notifications:\n${errorDetails}`)
      }
      
      return {
        total: totalToSend,
        sent: results.length,
        errors: errors.length,
        errorDetails: errors
      }
    },
    onStoreGroupChange() {
      // just for validation trigger
      this.$refs.form.validate()
    },
    removeStoreGroup(index) {
      this.selectedStoreGroups.splice(index, 1)
      this.$refs.form.validate()
    },
    async fetchStoreGroups() {
      this.loadingStoreGroups = true
      try {
        const { data } = await Search.customerSearch('STORE_GROUP')
        if (data?.length > 0) {
          this.storeGroups = data
            .filter(party => party.party_type === 'STORE_GROUP')
            .map(party => ({
              text: party.party_name,
              value: party.party_id
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
        }
      } catch (error) {
        console.error('Failed to fetch store groups:', error)
        this.showSnackbar('error', 'Failed to load store groups')
      } finally {
        this.loadingStoreGroups = false
      }
    },
    async fetchNotificationHistory(limit = 100, offset = 0) {
      this.historyLoading = true
      try {
        const response = await notification.getAllHistory(limit, offset)
        this.notificationHistory = response.data || []
      } catch (error) {
        console.error('Failed to fetch notification history:', error)
        this.showSnackbar('error', 'Failed to load notification history')
      } finally {
        this.historyLoading = false
      }
    },
    resetForm() {
      this.notification = {
        title: '',
        message: ''
      }
      this.selectedStoreGroups = []
      this.$refs.form.resetValidation()
    },
    showSnackbar(color, text) {
      this.snackbarColor = color
      this.snackbarText = text
      this.snackbar = true
    },
    viewNotification(notification) {
      this.selectedNotification = notification
      this.viewDialog = true
    },
    getStatusColor(status) {
      const colors = {
        Sent: 'success',
        Scheduled: 'info',
        Draft: 'grey',
        Failed: 'error',
        Pending: 'warning'
      }
      return colors[status] || 'grey'
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }
  },
  mounted() {
    this.fetchStoreGroups()
  }
}
</script>

<style scoped>
.notifications {
  max-width: 1200px;
}

.notification-card {
  width: 100%;
}

.notification-history-card {
  width: 100%;
}
</style>
